import {
  init,
  browserTracingIntegration,
  replayIntegration,
  setTag,
  type Event,
} from '@sentry/browser';
import { filterSentryEvent } from './piiFilter';

/**
 * Detect portal type based on hostname
 * Returns 'staff' if hostname contains 'staff', otherwise 'advisor'
 */
function getPortalType(): 'staff' | 'advisor' {
  return window.location.hostname.includes('staff') ? 'staff' : 'advisor';
}

/**
 * Get human-readable environment name
 */
function getEnvironmentName(): string {
  const mode = import.meta.env.MODE;
  const hostname = window.location.hostname;

  // Map Vite MODE to user-friendly names
  if (mode === 'production' || import.meta.env.PROD) {
    return 'production';
  } else if (hostname.includes('staging') || hostname.includes('stg')) {
    return 'staging';
  } else if (hostname.includes('local')) {
    return 'local';
  } else {
    return 'development';
  }
}

export const initSentry = () => {
  init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enabled: import.meta.env.VITE_SENTRY_ENABLED === undefined
      ? true
      : import.meta.env.VITE_SENTRY_ENABLED === 'true',
    integrations: [
      browserTracingIntegration({
        // Track navigation and user interactions for performance monitoring
        traceFetch: true,
        traceXHR: true,

        // Enable automatic instrumentation for web vitals
        enableLongTask: true,
        enableInp: true,
      }),
      replayIntegration({
        // Mask all text and user input by default for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring - tracks P95 latency for dashboard
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // We filter PII manually in beforeSend, so we don't need Sentry's default PII scrubbing
    sendDefaultPii: false,

    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,

    // Set initial context with portal type and environment
    initialScope: {
      tags: {
        portal_type: getPortalType(),
        environment_name: getEnvironmentName(),
      },
    },

    beforeSend(event: Event) {
      // Filter out known non-critical errors
      if (event.exception?.values?.[0]?.value?.includes('ResizeObserver')) {
        return null;
      }

      // Apply PII filtering to all events before sending to Sentry
      // This ensures no sensitive data (names, emails, SSNs, account numbers, etc.) is logged
      // while preserving safe ID fields (userId, transactionId, etc.) for debugging
      const scrubbedEvent = filterSentryEvent(event);

      // Ensure portal_type and environment_name are always set
      // (in case initialScope doesn't apply to all events)
      if (!scrubbedEvent.tags) {
        scrubbedEvent.tags = {};
      }
      scrubbedEvent.tags.portal_type = getPortalType();
      scrubbedEvent.tags.environment_name = getEnvironmentName();

      return scrubbedEvent;
    },
  });

  // Set global tags that will be included in all future events
  setTag('portal_type', getPortalType());
  setTag('environment_name', getEnvironmentName());
};
